/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  Action,
  AnyAction,
  PreloadedState,
  Reducer,
  Store,
  StoreEnhancer,
  ThunkAction,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

/*
The undo store enhancer works by augmenting an application's state to store not only the current version, but
also past versions and potentially future versions. However it doesn't take a snapshot every time an action is
dispatched, but rather only when an action or thunk wrapped in `recordHistory` is dispatched. This allows
undo/redo to work as a user would expect, undoing operations atomically, not little by little for operations that
kick off async work.

In order to give the user a good experience when working with async actions, the enhancer will try to preserve
any asynchronous effects of an operation that a user initiates across undo + redo. It does this by applying async
actions to past and future versions of the state alongside the present version of the state, provided the action had
been initiated by the time the past/future snapshot was created. Whenever an action or thunk is dispatched with
`recordHistory`, a new ID corresponding to the user intent is generated. A snapshot of the present state is taken
and stored with the other past snapshots. In addition to the state, the snapshot also stores the set of user intents
that had been taken up to that point, including the new one created in `recordHistory`. `recordHistory` then adds
the intent ID any action dispatched as part of the thunk it wraps. This allows the enhancer to know which past
snapshots to apply async actions to. Async actions won't be applied to snapshots created before their thunk was
first dispatched.

When a user undos, the present state is snapshotted into the future, and a past state is moved into the present.
Because the past state had been receiving async updates, anything that had finished processing in the present
should not require any further processing. Similarly, if a user undoes before a thunk finishes processing, it will
continue to process "in the future", so that if the user decides to redo, it won't have to start over. If a user
takes an action other than redoing while there is a stored future state, the stored future is erased because it
can no longer be guaranteed that it can be applied without conflict.
 */

interface StateSlice<S> {
  state: S;
  intents: Record<string, boolean>;
}

type History<S> = S & {
  __meta: {
    past: StateSlice<S>[];
    future: StateSlice<S>[];
    intents: Record<string, boolean>;
  };
};

const dispatchWrapper = <S, E, A extends Action>(
  f: (action: A) => A,
  dispatch: ThunkDispatch<S, E, A>,
  getState: () => S,
  extraArg: E
): ThunkDispatch<S, E, A> => {
  return <R>(actionCreator: A | ThunkAction<R, S, E, A>): A | R => {
    if (typeof actionCreator === "function") {
      return actionCreator(
        dispatchWrapper(f, dispatch, getState, extraArg),
        getState,
        extraArg
      );
    } else {
      return dispatch(f(actionCreator));
    }
  };
};

type CreateIntentAction = { type: "CREATE_INTENT"; intentId: string };
type FinishIntentAction = { type: "FINISH_INTENT"; intentId: string };
type UndoAction = { type: "UNDO" };
type RedoAction = { type: "REDO" };
type UndoAddedActions =
  | CreateIntentAction
  | FinishIntentAction
  | UndoAction
  | RedoAction;

type EnhancedAction<A extends Action<string>> = A & { __intentId?: string };
type UndoAllActions<A extends Action<string>> =
  | UndoAddedActions
  | EnhancedAction<A>;

export function recordHistory<A extends Action>(
  action: A
): ThunkAction<Promise<A>, any, any, A>;
export function recordHistory<R, S, E, A extends Action>(
  asyncAction: ThunkAction<Promise<R>, S, E, A>
): ThunkAction<Promise<R>, S, E, A>;
export function recordHistory<R, S, E, A extends Action>(
  actionCreator: A | ThunkAction<Promise<R>, S, E, A>
): ThunkAction<Promise<R | A>, S, E, A> {
  return async (
    dispatch: ThunkDispatch<S, E, A | UndoAddedActions>,
    getState: () => S,
    extraArg: E
  ): Promise<R | A> => {
    const intentId = uuidv4();

    await dispatch({
      type: "CREATE_INTENT",
      intentId: intentId,
    });

    const wrap = (action: AnyAction): AnyAction => {
      return {
        ...action,
        __intentId: intentId,
      };
    };

    const newDispatch: ThunkDispatch<S, E, A> = dispatchWrapper(
      wrap,
      dispatch,
      getState,
      extraArg
    );

    const result = await newDispatch(actionCreator);

    await dispatch({
      type: "FINISH_INTENT",
      intentId: intentId,
    });

    return result;
  };
}

export const undoAction = (): UndoAction => {
  return {
    type: "UNDO",
  };
};

export const redoAction = (): RedoAction => {
  return {
    type: "REDO",
  };
};

export const canUndoSelector: <S>(state: History<S>) => boolean = (state) => {
  return state.__meta.past.length > 0;
};

export const canRedoSelector: <S>(state: History<S>) => boolean = (state) => {
  return state.__meta.future.length > 0;
};

type UndoEnhancerOptions<S, E, A extends Action> = {
  postChange?: ThunkAction<any, S, E, A>;
};

export const undoEnhancer: <S, E, A extends Action>(
  options?: UndoEnhancerOptions<S, E, A>
) => StoreEnhancer =
  (options = {}) =>
  (createStore) =>
  <S, A extends Action<string>>(
    reducer: Reducer<S, A>,
    initialState?: PreloadedState<S>
  ): Store<S & {}, A> => {
    const undoableState: PreloadedState<History<S>> = {
      ...initialState,
      __meta: {
        past: [],
        future: [],
        intents: {},
      },
    } as any as PreloadedState<History<S>>;

    const callPostChangeCallback = (): void => {
      if (options.postChange) {
        setTimeout(() => {
          dispatchWrapper(
            (x) => x,
            store.dispatch,
            store.getState,
            undefined
          )(options.postChange as any);
        });
      }
    };

    const undoableReducer = (
      state: History<S>,
      action: UndoAllActions<A>
    ): History<S> => {
      const { __meta, ...presentState } = state;

      switch (action.type) {
        case "CREATE_INTENT":
          return {
            ...(presentState as any as S),
            __meta: {
              future: [],
              past: __meta.past.concat({
                state: presentState as any as S,
                intents: __meta.intents,
              } as StateSlice<S>),
              intents: {
                ...__meta.intents,
                [(action as CreateIntentAction).intentId]: true,
              },
            },
          };
        case "FINISH_INTENT":
          if (__meta.intents[(action as FinishIntentAction).intentId]) {
            callPostChangeCallback();
          }
          return state;
        case "UNDO": {
          const { __meta, ...presentState } = state;
          const [...oldStates] = __meta.past;
          const mostRecentState = oldStates.pop();
          if (!mostRecentState) {
            return state;
          }

          callPostChangeCallback();
          return {
            ...mostRecentState.state,
            __meta: {
              future: __meta.future.concat({
                state: presentState as any as S,
                intents: __meta.intents,
              }),
              past: oldStates,
              intents: mostRecentState.intents,
            },
          };
        }
        case "REDO": {
          const { __meta, ...presentState } = state;
          const [...futureStates] = __meta.future;
          const leastFutureState = futureStates.pop();
          if (!leastFutureState) {
            return state;
          }

          callPostChangeCallback();
          return {
            ...leastFutureState.state,
            __meta: {
              future: futureStates,
              past: __meta.future.concat({
                state: presentState as any as S,
                intents: __meta.intents,
              }),
              intents: leastFutureState.intents,
            },
          };
        }
        default: {
          const nextState = reducer(presentState as any as S, action as A);

          const update = ({
            state: someState,
            intents,
          }: StateSlice<S>): StateSlice<S> => {
            const enhancedAction: EnhancedAction<A> =
              action as EnhancedAction<A>;
            if (
              !enhancedAction.__intentId ||
              intents[enhancedAction.__intentId]
            ) {
              return {
                state: reducer(someState, action as A),
                intents,
              };
            } else {
              return { state: someState, intents };
            }
          };

          return {
            ...nextState,
            __meta: {
              future: __meta.future.map(update),
              past: __meta.past.map(update),
              intents: __meta.intents,
            },
          };
        }
      }
    };

    const store = createStore(undoableReducer, undoableState) as Store<
      S & {},
      A
    >;

    return store;
  };
